// @flow

import React from 'react';
import moment from 'moment';
import { Translate } from 'react-redux-i18n';
import type { BusinessPlanBean } from '../../../../types/geminiType';
import BPKpiSelector from './BPKpiSelector';
import CurrencyConvertor from '../../../common/CurrencyConvertor';
import PercentageChange from '../../../common/PercentageChange';
import BPBoldKpi from '../../../common/BPBoldKpi';
import RippleButton from '../../../../components/RippleButton';
import { getCurrentPcId } from '../../../../utils/urlUtils';
import { BUSINESS_PLAN_CATEGORIES } from '../../../../const';
import { bestRetailerBPUrl } from '../../../../utils/routerUtils';

type Props = {
  history: {
    push: Function,
  },
  businessPlan: BusinessPlanBean,
  onClose: Function,
  onQuitBusinessPlanView: Function,
}

class BusinessPlanSideData extends React.PureComponent<Props> {
  render() {
    return (
      <div className="gemini-store-data-container gemini-store-data-container--overflow">
        <div className="store-name-container">
          <div className="placeholder placeholder--absolute">
            <button
              onClick={this.props.onClose}
              className="exit-button"
            >
              <i className="fal fa-chevron-right right-chevron" />
            </button>
          </div>
          <div className="store-name store-name--center">
            {this.props.businessPlan.operation_type_description}
          </div>
          <div className="placeholder" />
          <i onClick={this.props.onQuitBusinessPlanView} className="fa fa-times-circle close" />
        </div>
        <div className="analysis-container-block">
          {
            this.props.businessPlan.operation_type_description === BUSINESS_PLAN_CATEGORIES.LETTING_RELETTING && (
              <div className="action-buttons-container">
                <div className="btn-apollo-container mobile-hide">
                  <RippleButton
                    className="apollo-button-contained rounded-button"
                    onClick={() => {
                      // $FlowFixMe
                      this.props.history.push(bestRetailerBPUrl(this.props.businessPlan.codstr, getCurrentPcId(), this.props.businessPlan.tenant));
                    }}
                  >
                    <i className="fas fa-medal" />
                    <Translate value="SIDE_DATA.apollo" />
                  </RippleButton>
                </div>
              </div>
            )
          }
          <div className="data-section-header">
            <Translate value="SIDE_DATA.description" />
          </div>
          <div className="kpi-selector-container">
            <table>
              <thead>
                <tr>
                  <th />
                  <th><Translate value="SIDE_DATA.BUSINESS_PLAN.oldContract" /></th>
                  <th><Translate value="SIDE_DATA.BUSINESS_PLAN.newContract" /></th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <BPKpiSelector
                  label="SIDE_DATA.BUSINESS_PLAN.oldTenant"
                  oldValue={this.props.businessPlan.old_tenant}
                  newValue={this.props.businessPlan.tenant}
                />
                <BPKpiSelector
                  label="SIDE_DATA.BUSINESS_PLAN.mgr"
                  oldValue={<CurrencyConvertor value={this.props.businessPlan.old_mgr} />}
                  newValue={<CurrencyConvertor value={this.props.businessPlan.new_mgr} />}
                />
                <BPKpiSelector
                  label=""
                  newValue={
                    <span>
                      <BPBoldKpi label="SIDE_DATA.BUSINESS_PLAN.erv" value={this.props.businessPlan.erv} />
                      <BPBoldKpi label="SIDE_DATA.BUSINESS_PLAN.trv" value={this.props.businessPlan.trv} />
                      <BPBoldKpi label="SIDE_DATA.BUSINESS_PLAN.rrv" value={this.props.businessPlan.rrv} />
                    </span>
                  }
                />
                <BPKpiSelector
                  label="SIDE_DATA.BUSINESS_PLAN.mgrUplift"
                  newValue={
                    <CurrencyConvertor
                      value={this.props.businessPlan.new_mgr - this.props.businessPlan.old_mgr}
                    />
                  }
                  optionalValue={
                    <PercentageChange
                      newValue={this.props.businessPlan.new_mgr}
                      oldValue={this.props.businessPlan.old_mgr}
                      digit={1}
                    />
                  }
                />
                <BPKpiSelector
                  label="SIDE_DATA.BUSINESS_PLAN.effectiveRent"
                  newValue={<CurrencyConvertor value={this.props.businessPlan.effective_rent} />}
                />
                <BPKpiSelector
                  label="SIDE_DATA.BUSINESS_PLAN.effectiveRentUplift"
                  newValue={<CurrencyConvertor value={this.props.businessPlan.effective_rent_uplift} />}
                  optionalValue={
                    <PercentageChange
                      newValue={this.props.businessPlan.effective_rent}
                      oldValue={-(this.props.businessPlan.effective_rent_uplift - this.props.businessPlan.effective_rent)}
                      digit={1}
                    />
                  }
                />
                <BPKpiSelector
                  label="SIDE_DATA.BUSINESS_PLAN.rentIncentives"
                  newValue={<CurrencyConvertor value={this.props.businessPlan.rent_incentives} />}
                />
                <BPKpiSelector
                  label="SIDE_DATA.BUSINESS_PLAN.foc"
                  newValue={<CurrencyConvertor value={this.props.businessPlan.fitting_out_costs} />}
                />
                <tr />
                <BPKpiSelector
                  label="SIDE_DATA.BUSINESS_PLAN.rentFreePeriod"
                  newValue={<CurrencyConvertor value={this.props.businessPlan.rent_free_period} />}
                />
                <BPKpiSelector
                  label="SIDE_DATA.BUSINESS_PLAN.stepRent"
                  labelVariables={{ number: '1' }}
                  newValue={<CurrencyConvertor value={this.props.businessPlan.step_rent_1} />}
                />
                <BPKpiSelector
                  label="SIDE_DATA.BUSINESS_PLAN.stepRent"
                  labelVariables={{ number: '2' }}
                  newValue={<CurrencyConvertor value={this.props.businessPlan.step_rent_2} />}
                />
                <BPKpiSelector
                  label="SIDE_DATA.BUSINESS_PLAN.stepRent"
                  labelVariables={{ number: '3' }}
                  newValue={<CurrencyConvertor value={this.props.businessPlan.step_rent_3} />}
                />
                <tr />
                <BPKpiSelector
                  label="SIDE_DATA.BUSINESS_PLAN.signatureDate"
                  newValue={moment(this.props.businessPlan.signature_date).format('ll')}
                />
                <BPKpiSelector
                  label="SIDE_DATA.BUSINESS_PLAN.effectiveDate"
                  newValue={moment(this.props.businessPlan.effective_date).format('ll')}
                />
                <tr />
                <BPKpiSelector
                  label="SIDE_DATA.BUSINESS_PLAN.operationId"
                  newValue={this.props.businessPlan.operation_id}
                />
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }
}

export default BusinessPlanSideData;
